import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { resourceCache, useAsyncResource } from 'use-async-resource';
import { Button } from 'react-bootstrap';
import { CustomTable } from '../../components/CustomTable';
import { CustomToastContent } from '../../components/CustomToastContent';
import { NoDataComponent } from '../../components/NoDataComponent';
import { PassportsTableInnerProps } from '../../models/table-models';
import { AnyPassport, Passport } from '../../models/warehouse-models';
import { useGetConsumableServiceGroups, useGetDeviceGroups, useGetEssencePriceGroups, useGetReedInstallationGroups } from '../../utils/configuration-hooks';
import { TableHeaders } from '../../utils/Enums/TableHeaders';
import { toastOptionsTopRight } from '../../utils/Enums/ToastOptions';
import {
    getDevicesModel,
    useDeletePassport,
    useGetAllBatteriesPassports,
    useGetAllOilPassports,
    useGetAllPassportTypes,
    useGetAllShortDevicePassports,
    useGetAllShortPassports,
    updatePassportArchived,
} from '../../utils/warehouses-hooks';
import { PaginationComponent } from '../../components/Pagination';
import { Role } from '../../utils/Enums/Roles';
import { AddEditPassport } from './AddEditPassport';

const passportTypeOrder: Record<string, number> = {
    DEVICE: 0,
    OIL: 1,
    BATTERY: 2,
    CONTAINER: 3,
    OTHER: 3,
};

const passportTableHeaders = [
    TableHeaders.name,
    TableHeaders.type,
    TableHeaders.price,
    TableHeaders.priceForInvoice,
    TableHeaders.priceForSale,
    TableHeaders.minimumDeposit,
    TableHeaders.model,
    TableHeaders.deviceGroup,
    TableHeaders.essencePriceGroup,
    TableHeaders.containerType,
    TableHeaders.capacityMl,
    TableHeaders.description,
];

export const sortPassportsByType = (items: AnyPassport[]) => {
    return [...items].sort((a, b) => {
        const typeDiff = (passportTypeOrder[a.type] ?? passportTypeOrder.OTHER) - (passportTypeOrder[b.type] ?? passportTypeOrder.OTHER);

        if (typeDiff !== 0) {
            return typeDiff;
        }

        return a.name.localeCompare(b.name);
    });
};

export const PassportTable = ({
    getEntities,
    pagination,
    refetchEntitiesHook,
    setSelectedItem,
    archived,
}: PassportsTableInnerProps) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const { items, pager } = getEntities();
    const [readDeviceGroups] = useAsyncResource(useGetDeviceGroups, []);
    const [readEssencePriceGroups] = useAsyncResource(useGetEssencePriceGroups, []);
    const [readReedInstallationGroups] = useAsyncResource(useGetReedInstallationGroups, []);
    const [readConsumableServiceGroups] = useAsyncResource(useGetConsumableServiceGroups, []);
    const deviceGroups = readDeviceGroups();
    const essencePriceGroups = readEssencePriceGroups();
    const reedInstallationGroups = readReedInstallationGroups();
    const consumableServiceGroups = readConsumableServiceGroups();
    const deviceGroupsByNumber = new Map(deviceGroups.map((deviceGroup) => [deviceGroup.groupNumber, deviceGroup]));
    const priceGroupsByNumber = new Map(essencePriceGroups.map((priceGroup) => [priceGroup.groupNumber, priceGroup]));
    const reedGroupsByNumber = new Map(reedInstallationGroups.map((group) => [group.groupNumber, group]));
    const consumableGroupsByNumber = new Map(consumableServiceGroups.map((group) => [group.groupNumber, group]));
    const sortedItems = sortPassportsByType(
        typeof archived === 'boolean'
            ? items.filter((item) => (item.archived ?? false) === archived)
            : items
    );

    const renderGroup = (
        number: number | undefined,
        groups: Map<number, { groupNumber: number; color: string }>,
        kind: string
    ) => {
        const group = number ? groups.get(number) : undefined;

        if (!group) {
            return '';
        }

        return (
            <span
                className='d-inline-flex align-items-center gap-2 px-2 py-1'
                style={{ backgroundColor: '#f7f9fc', border: '1px solid #e1e6ed', borderRadius: 12, whiteSpace: 'nowrap' }}
            >
                <span
                    style={{
                        backgroundColor: group.color,
                        border: '1px solid rgba(0, 0, 0, 0.2)',
                        borderRadius: '50%',
                        display: 'inline-block',
                        flexShrink: 0,
                        height: 12,
                        width: 12,
                    }}
                />
                <span className='small fw-semibold text-muted'>{kind}</span>
                <span className='fw-semibold'>№ {group.groupNumber}</span>
            </span>
        );
    };

    const renderInstallationGroup = (item: AnyPassport) => item.type === 'DEVICE'
        ? renderGroup(item.deviceGroupId, deviceGroupsByNumber, t('device').toUpperCase())
        : renderGroup(item.reedInstallationGroupId, reedGroupsByNumber, 'REED');

    const renderPriceOrServiceGroup = (item: AnyPassport) => item.type === 'OIL'
        ? renderGroup(item.essencePriceGroupId, priceGroupsByNumber, t('oil').toUpperCase())
        : renderGroup(item.consumableServiceGroupId, consumableGroupsByNumber, t('reedService').toUpperCase());

    const useDelete = async (passport: Passport) => {
        await useDeletePassport(passport);
        toast.success(<CustomToastContent content={[t('passportDelete')]} />, toastOptionsTopRight);
    };

    const clearPassportCaches = () => {
        resourceCache(getDevicesModel).clear();
        resourceCache(useGetAllShortDevicePassports).clear();
        resourceCache(useGetAllShortPassports).clear();
        resourceCache(useGetAllOilPassports).clear();
        resourceCache(useGetAllBatteriesPassports).clear();
        resourceCache(useGetAllPassportTypes).clear();
    };

    const updateArchived = async (
        passport: AnyPassport,
        setShowModal: React.Dispatch<React.SetStateAction<boolean>>
    ) => {
        const nextArchived = !(passport.archived ?? false);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        await updatePassportArchived({
            id: passport.id,
            archived: nextArchived,
        });
        clearPassportCaches();
        await refetchEntitiesHook();
        setShowModal(false);
        toast.success(
            <CustomToastContent content={[t(nextArchived ? 'passportArchived' : 'passportUnarchived')]} />,
            toastOptionsTopRight
        );
    };

    const renderModel = ({ type, model, forDeviceModel }: AnyPassport) => {
        if (type === 'BATTERY') {
            return model;
        }

        if (type === 'OIL') {
            return forDeviceModel && forDeviceModel.trim().length > 0 ? forDeviceModel : t('all');
        }

        return model ?? '';
    };

    return (
        <>
            {sortedItems.length > 0 ? (
                <>
                    <CustomTable
                        columns={{
                            headings: passportTableHeaders,
                            items: sortedItems.map((item) => [
                                item.name,
                                t(item.type.toLowerCase()),
                                item.price.toFixed(2),
                                item.priceForInvoice.toFixed(2),
                                item.priceForSale.toFixed(2),
                                item.type === 'DEVICE' || item.type === 'OTHER' ? (item.minimumDeposit ?? 0).toFixed(2) : '',
                                renderModel(item),
                                renderInstallationGroup(item),
                                renderPriceOrServiceGroup(item),
                                item.containerType ? t(item.containerType) : '',
                                item.capacityMl ?? '',
                                item.description ?? '',
                            ]),
                        }}
                        editComponent={AddEditPassport}
                        editFooterAction={({ item, setShowModal }) => (
                            <Button
                                variant={(item.archived ?? false) ? 'primary' : 'warning'}
                                onClick={() => updateArchived(item, setShowModal)}
                            >
                                {t((item.archived ?? false) ? 'unarchivePassport' : 'archivePassport')}
                            </Button>
                        )}
                        actionHook={useDelete}
                        viewComponent={(id) => {
                            const item = sortedItems.find((value) => value.id + '' === id);

                            if (item && item.model !== undefined) {
                                item.forModel = item.model;
                            }

                            setSelectedItem(item);
                        }}
                        tableData={sortedItems}
                        refetchEntitiesHook={refetchEntitiesHook}
                        accessActionRoles={[Role.ADMIN, Role.INSTALLER]}
                        accessEditRoles={[Role.ADMIN, Role.INSTALLER]}
                    />
                    <PaginationComponent recordsCount={pager.totalCount} pagination={pagination} />
                </>
            ) : (
                <NoDataComponent noDataText={t('passports')} />
            )}
        </>
    );
};
